import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalculator } from '../../context/CalculatorContext';
import { jsPDF } from 'jspdf';
import styles from './Results.module.css';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Gasolina from '../../assets/gasolina.svg';
import Fuego from '../../assets/fuego.svg';
import CO2 from '../../assets/co2.svg';
import Carro from '../../assets/Carro.svg';
import Arbol from '../../assets/Arbol.svg';
import backgroundA4 from '../../assets/A4.svg';

function Results() {
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { resultados, email, empresa, reiniciar } = useCalculator();

  console.log('📊 Results - Resultados recibidos:', resultados);

  // Función para formatear números con separadores de miles y decimales
  const formatearNumero = (numero) => {
    const num = typeof numero === 'string' ? parseFloat(numero) : numero;
    
    // Separar parte entera y decimal
    const partes = num.toFixed(2).split('.');
    const parteEntera = partes[0];
    const parteDecimal = partes[1];
    
    // Agregar separador de miles manualmente
    const conSeparadorMiles = parteEntera.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Retornar con coma como separador decimal
    return `${conSeparadorMiles},${parteDecimal}`;
  };

  const handleNewCalculation = () => {
    reiniciar();
    navigate('/');
  };

  const handleDownloadPDF = () => {
    generarPDF();
  };

  // Función para generar el PDF replicando el diseño visual
  const generarPDF = async () => {
    // Función mejorada para convertir SVG a una URL de datos con mayor resolución
    const svgToDataUrl = (svgUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Aumentar la resolución multiplicando las dimensiones originales
          const scale = 2; // Factor de escala para mejorar la resolución
          const canvas = document.createElement('canvas');
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          const ctx = canvas.getContext('2d');
          
          // Escalar el contexto para mantener las proporciones originales
          ctx.scale(scale, scale);
          ctx.drawImage(img, 0, 0);
          
          // Obtener la imagen en alta resolución
          resolve(canvas.toDataURL('image/png', 1.0)); // Calidad máxima (1.0)
        };
        img.src = svgUrl;
      });
    };
    
    // Función específica para la imagen de fondo con mayor resolución
    const backgroundToDataUrl = (svgUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          // Usar dimensiones específicas para A4 en alta resolución
          const canvas = document.createElement('canvas');
          // A4 en puntos por pulgada (dpi) más alto para impresión: 210mm x 297mm a 300dpi
          canvas.width = 2480; // ~210mm a 300dpi
          canvas.height = 3508; // ~297mm a 300dpi
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png', 1.0));
        };
        img.src = svgUrl;
      });
    };

    // Convertir SVGs a DataURLs
    const gasolinaIcon = await svgToDataUrl(Gasolina);
    const fuegoIcon = await svgToDataUrl(Fuego);
    const co2Icon = await svgToDataUrl(CO2);
    const carroIcon = await svgToDataUrl(Carro);
    const arbolIcon = await svgToDataUrl(Arbol);
    const backgroundImg = await backgroundToDataUrl(backgroundA4);

    // Crear PDF con soporte para caracteres especiales
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      floatPrecision: 16
    });
    
    // Añadir fuente con soporte para caracteres especiales
    pdf.addFont('helvetica', 'normal');
    pdf.addFont('helvetica', 'bold');
    
    const pageWidth = 210;
    const margin = 15;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = 50;

    // Colores corporativos (RGB)
    const colors = {
      primary: [19, 34, 106],      // #13226a - Azul oscuro
      lightBlue: [70, 172, 231],   // #46ace7 - Azul claro (gasolina)
      green: [14, 226, 116],       // #0ee274 - Verde (gas)
      lime: [190, 224, 0],         // #bee000 - Verde lima (reducción)
      lightGray: [233, 233, 241],  // #e9e9f1 - Gris claro (headers)
      white: [255, 255, 255],
      black: [0, 0, 0]
    };
    
    // Añadir imagen de fondo A4 con compresión reducida
    pdf.addImage(backgroundImg, 'PNG', 0, 0, 210, 297, undefined, 'NONE');
    
    // Título principal (CORREGIDO: Cálculo)
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('INFORME DE TRANSICIÓN ENERGÉTICA', pageWidth / 2, yPos, { align: 'center' });
    yPos += 7;

    // ==================== ENCABEZADO CON DATOS DEL USUARIO ====================
    const fechaActual = new Date().toLocaleDateString('es-ES');
    
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.text(`Fecha: ${fechaActual}`, margin, yPos);
    
    yPos += 6;
    
    pdf.text('Señores:', margin, yPos);
    yPos += 4;
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${empresa}`, margin, yPos);
    yPos += 4;
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${email}`, margin, yPos);
    
    yPos += 6;
    
    // Texto introductorio
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    const introText = `Atendiendo su amable solicitud, nos permitimos presentar análisis comparativo de huella de carbono entre el combustible actual ${resultados.combustibleNombre} y el Gas Natural.

Como contexto se aclara que los combustibles Gas Natural y otros combustibles no son comparables directamente por su composición química y sistemas de medición.`;
    
    const introLines = pdf.splitTextToSize(introText, contentWidth);
    pdf.text(introLines, margin, yPos);
    
    yPos += introLines.length * 3 + 3;

    // ==================== TÍTULO PRINCIPAL (CORREGIDO: CÁLCULO) ====================
    pdf.setFillColor(...colors.primary);
    pdf.roundedRect(margin + 35, yPos, contentWidth - 70, 12, 9, 9, 'F');
    pdf.setTextColor(...colors.white);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(15);
    pdf.text('RESULTADOS DEL CÁLCULO', pageWidth / 2, yPos + 9, { align: 'center' });
    
    yPos += 14;

    // ==================== HEADER MESSAGE 1 ====================
    pdf.setFillColor(...colors.lightGray);
    pdf.roundedRect(margin + 10, yPos, contentWidth - 20, 9, 6, 6, 'F');
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('Esta es tu huella de CO2 actual vs si usaras gas natural', pageWidth / 2, yPos + 6, { align: 'center' });
    
    yPos += 12;

    // ==================== CARD 1: COMBUSTIBLE ACTUAL (ANCHO REDUCIDO) ====================
    const cardWidth = contentWidth - 40; // Ancho reducido para coincidir con la vista web
    const cardMarginX = (contentWidth - cardWidth) / 2 + margin; // Centrar card
    
    pdf.setFillColor(...colors.lightBlue);
    pdf.roundedRect(cardMarginX, yPos, cardWidth, 19, 8, 8, 'F');
    
    // Añadir ícono de gasolina
    pdf.addImage(gasolinaIcon, 'PNG', cardMarginX + 5, yPos + 3, 12, 12); 
    
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const titulo1 = `Emisiones de CO2 con ${resultados.combustibleNombre} (${formatearNumero(resultados.consumo)} ${resultados.unidad}/mes)`;
    const titulo1Lines = pdf.splitTextToSize(titulo1, 80);
    pdf.text(titulo1Lines, cardMarginX + 20, yPos + 7);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(formatearNumero(resultados.emisionesCO2Actuales), cardMarginX + cardWidth - 10, yPos + 12, { align: 'right' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text('kg CO2', cardMarginX + cardWidth - 10, yPos + 17, { align: 'right' });
    
    yPos += 21;

    // ==================== CARD 2: GAS NATURAL (ANCHO REDUCIDO) ====================
    pdf.setFillColor(...colors.green);
    pdf.roundedRect(cardMarginX, yPos, cardWidth, 19, 8, 8, 'F');
    
    // Añadir ícono de fuego
    pdf.addImage(fuegoIcon, 'PNG', cardMarginX + 5, yPos + 3, 12, 12);
    
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    const titulo2 = `Emisiones de CO2 con Gas Natural (${formatearNumero(resultados.volumenGasNatural)} m3/mes)`;
    const titulo2Lines = pdf.splitTextToSize(titulo2, 80);
    pdf.text(titulo2Lines, cardMarginX + 20, yPos + 7);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(formatearNumero(resultados.emisionesCO2GasNatural), cardMarginX + cardWidth - 10, yPos + 12, { align: 'right' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text('kg CO2', cardMarginX + cardWidth - 10, yPos + 17, { align: 'right' });
    
    yPos += 21;

    // ==================== CARD 3: REDUCCIÓN (ANCHO REDUCIDO, CORREGIDO: Reducción) ====================
    pdf.setFillColor(...colors.lime);
    pdf.roundedRect(cardMarginX, yPos, cardWidth, 24, 8, 8, 'F');
    
    // Añadir ícono de CO2
    pdf.addImage(co2Icon, 'PNG', cardMarginX + 5, yPos + 3, 12, 12);
    
    // RESALTADO: Título con mayor énfasis
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold'); // Cambiado de 'normal' a 'bold'
    pdf.setFontSize(10); // Aumentado de 8 a 10
    pdf.text('Reducción de emisiones', cardMarginX + 20, yPos + 7);
    
    // RESALTADO: Valor numérico más grande
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14); // Aumentado de 12 a 14
    pdf.text(formatearNumero(resultados.reduccionEmisiones), cardMarginX + cardWidth - 10, yPos + 10, { align: 'right' });
    
    // RESALTADO: Unidad con mayor peso
    pdf.setFont('helvetica', 'bold'); // Cambiado de 'normal' a 'bold'
    pdf.setFontSize(9); // Aumentado de 8 a 9
    pdf.text('kg CO2/mes', cardMarginX + cardWidth - 10, yPos + 15, { align: 'right' });
    
    // RESALTADO: Porcentaje más destacado (CORREGIDO: reducción)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16); // Aumentado de 14 a 16
    pdf.text(`${resultados.reduccionEmisionesPorcentaje}% de reducción`, pageWidth / 2, yPos + 22, { align: 'center' });
    
    yPos += 26;

    // ==================== HEADER MESSAGE 2 (CORREGIDO: énergético) ====================
    pdf.setFillColor(...colors.lightGray);
    pdf.roundedRect(margin + 10, yPos, contentWidth - 20, 9, 6, 6, 'F');
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    const headerText2 = 'A esto equivale que dejes de usar otro energético y te cambies a gas natural';
    pdf.text(headerText2, pageWidth / 2, yPos + 6, { align: 'center' });
    
    yPos += 11;

    // ==================== SUBTÍTULO (CORREGIDO: disminución) ====================
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text('La disminución de la huella de carbono de su empresa es equivalente a:', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 6;

    // ==================== EQUIVALENCIAS - VEHÍCULOS Y ÁRBOLES (TEXTO JUSTIFICADO CON MÁRGENES) ====================
    const equivCardWidth = (contentWidth / 2) - 7;
    const equivPadding = 8; // Padding interno aumentado para separar el texto de los bordes
    
    // CARD VEHÍCULOS
    pdf.setFillColor(...colors.lime);
    pdf.roundedRect(margin + 5, yPos, equivCardWidth, 22, 8, 8, 'F');
    
    // Añadir ícono de carro
    pdf.addImage(carroIcon, 'PNG', margin + 5 + equivPadding, yPos + 3, 14, 14);
    
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    // Aumentar margen desde el ícono
    pdf.text(formatearNumero(resultados.carrosEquivalentes), margin + 5 + equivPadding + 20, yPos + 10, { align: 'left' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    // JUSTIFICADO: Texto con align justify y márgenes aumentados
    const vehiculoText = 'Vehículo fuera de circulación durante un mes';
    const vehiculoMaxWidth = equivCardWidth - (equivPadding * 2) - 20; // Restar padding de ambos lados + espacio del ícono
    pdf.text(vehiculoText, margin + 5 + equivPadding + 20, yPos + 16, { 
      align: 'justify', 
      maxWidth: vehiculoMaxWidth 
    });

    // CARD ÁRBOLES
    pdf.setFillColor(...colors.lightBlue);
    pdf.roundedRect(margin + (contentWidth / 2) + 2, yPos, equivCardWidth, 22, 8, 8, 'F');
    
    // Añadir ícono de árbol
    pdf.addImage(arbolIcon, 'PNG', margin + (contentWidth / 2) + 2 + equivPadding, yPos + 3, 14, 14);
    
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    // Aumentar margen desde el ícono
    pdf.text(formatearNumero(resultados.arbolesEquivalentes), margin + (contentWidth / 2) + 2 + equivPadding + 20, yPos + 10, { align: 'left' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    // JUSTIFICADO: Texto con align justify y márgenes aumentados
    const arbolText = 'Árboles de eucalipto absorbiendo CO2 durante un mes';
    const arbolMaxWidth = equivCardWidth - (equivPadding * 2) - 20; // Restar padding de ambos lados + espacio del ícono
    pdf.text(arbolText, margin + (contentWidth / 2) + 2 + equivPadding + 20, yPos + 16, { 
      align: 'justify', 
      maxWidth: arbolMaxWidth 
    });
    
    yPos += 24;

    // ==================== NOTAS ADICIONALES ====================
    pdf.setFillColor(...colors.lightGray);
    pdf.roundedRect(margin + 10, yPos, contentWidth - 20, 9, 6, 6, 'F');
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('NOTAS ADICIONALES', pageWidth / 2, yPos + 6, { align: 'center' });
    
    yPos += 11;

    // Lista de notas (más compacta)
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    
    // Notas en una sola línea para ahorrar espacio (CORREGIDAS: Cálculos)
    pdf.text('• Cálculos con valores nominales sin costos ocultos', margin + 15, yPos);
    yPos += 4;
    pdf.text('• No se consideran eficiencias de combustibles o equipos', margin + 15, yPos);
    yPos += 4;
    pdf.text('• Resultados aproximados de carácter informativo', margin + 15, yPos);
    yPos += 8;

    // ==================== FIRMA ====================
    pdf.setTextColor(...colors.primary);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.text('ATENTAMENTE', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 5;
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.text('EFIGAS S.A E.S.P', pageWidth / 2, yPos, { align: 'center' });
    
    // Guardar el PDF
    pdf.save(`Resultados_Calculadora_${empresa.replace(/\s/g, '_')}.pdf`);
  };

  // Si no hay resultados, mostrar mensaje
  if (!resultados) {
    return (
      <div className={styles.results}>
        <Header />
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <h2 className={styles.title}>No hay resultados</h2>
            <p style={{ textAlign: 'center', padding: '20px' }}>
              Por favor realice un cálculo primero
            </p>
            <div className={styles.buttons}>
              <button 
                type="button" 
                className={styles.actionButton} 
                onClick={() => navigate('/')}
              >
                Ir al inicio
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={styles.results}>
      <Header />
      <div className={styles.content}>
        <div className={styles.formContainer}>
          <h2 className={styles.title}>Resultados del cálculo</h2>
          <div className={styles.resultsContent}>
            <div className={styles.headerMessage}>
              Esta es tu huella de CO2 actual vs si usaras gas natural
            </div>
            <div className={`${styles.resultCard} ${styles.cardGasolina}`}>
              <img src={Gasolina} alt="Combustible" className={styles.cardIcon} />
              <p className={styles.cardDescription}>
                Emisiones de CO2 con {resultados.combustibleNombre} ({formatearNumero(resultados.consumo)} {resultados.unidad}/mes)
              </p>
              <p className={styles.cardValue}>{formatearNumero(resultados.emisionesCO2Actuales)} kgCO2</p>
            </div>
            <div className={`${styles.resultCard} ${styles.cardGas}`}>
              <img src={Fuego} alt="Gas Natural" className={styles.cardIcon} />
              <p className={styles.cardDescription}>Emisiones de CO2 con Gas Natural ({formatearNumero(resultados.volumenGasNatural)} m³/mes)</p>
              <p className={styles.cardValue}>{formatearNumero(resultados.emisionesCO2GasNatural)} kg CO2</p>
            </div>
            <div className={`${styles.resultCard} ${styles.cardReduccion}`}>
              <img src={CO2} alt="CO2" className={styles.cardIcon} />
              <p className={styles.cardDescription}>Reducción de emisiones</p>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                <p className={styles.cardValue}>{formatearNumero(resultados.reduccionEmisiones)} kg CO2/mes</p>
                <p style={{
                  fontSize: '28px',
                  fontWeight: 'bold',
                  color: '#13226a',
                  margin: '0',
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}>
                  {resultados.reduccionEmisionesPorcentaje}% de reducción
                </p>
              </div>
            </div>
            <div className={styles.headerMessage}>
              A esto equivale que dejes de usar otro energético y te cambies a gas natural
            </div>
            <p className={styles.subText}>
              La disminución de la huella de carbono de su empresa es equivalente a:
            </p>
            <div className={styles.equivalenceRow}>
              <div className={`${styles.equivalenceCard} ${styles.cardVehicle}`}>
                <img src={Carro} alt="Vehículo" className={styles.equivalenceIcon} />
                <div className={styles.equivalenceInfo}>
                  <p className={styles.equivalenceValue}>{formatearNumero(resultados.carrosEquivalentes)}</p>
                  <p className={styles.equivalenceText}>Vehículo fuera de circulación durante un mes</p>
                </div>
              </div>
              <div className={`${styles.equivalenceCard} ${styles.cardTree}`}>
                <img src={Arbol} alt="Árbol" className={styles.equivalenceIcon} />
                <div className={styles.equivalenceInfo}>
                  <p className={styles.equivalenceValue}>{formatearNumero(resultados.arbolesEquivalentes)}</p>
                  <p className={styles.equivalenceText}>Árboles de eucalipto absorbiendo CO2 durante un mes</p>
                </div>
              </div>
            </div>
            <p className={styles.footerText}>
              Estas equivalencias representan el impacto ambiental positivo mensual al cambiar a gas natural
            </p>
            <div className={styles.headerMessage}>
              Beneficios adicionales
            </div>
            <p className={styles.benefitTitle}>Mayor eficiencia energética</p>
            <div className={styles.benefitBox}>Menor mantenimiento de equipos</div>
            <p className={styles.benefitTitle}>Cumplimiento con regulaciones ambientales</p>
            <div className={styles.benefitBox}>Mejora en la imagen corporativa</div>
            <p className={styles.benefitTitle}>Reducción de la huella de carbono</p>
            <div className={styles.benefitBox}>Menos contaminantes atmosféricos emitidos mejorando la calidad del aire</div>
          </div>
          <div className={styles.buttons}>
            <button type="button" className={styles.actionButton} onClick={handleNewCalculation}>
              Realizar nuevo cálculo
            </button>
            <button type="button" className={styles.actionButton} onClick={handleDownloadPDF}>
              Descargar informe PDF
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Results;